import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Radio, X, Send, Users, VolumeX, Volume2, Heart, MessageCircle } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { RTC_CONFIG, waitIceComplete } from "@/lib/webrtc";
import { LiveReactionsLayer, LiveReactionsBar } from "./LiveReactions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * LiveViewerDialog — public viewer for a live session.
 * Establishes a real WebRTC peer connection to the broadcaster via HTTP signaling.
 * Falls back to placeholder if the broadcaster's stream is not yet available.
 */
export default function LiveViewerDialog({ liveId, onClose }) {
  const [live, setLive] = useState(null);
  const [chatInput, setChatInput] = useState("");
  const [connState, setConnState] = useState("connecting"); // connecting | connected | ended
  const [muted, setMuted] = useState(true);
  const [chatCollapsed, setChatCollapsed] = useState(false);
  const [likeBurst, setLikeBurst] = useState(0);
  const pollRef = useRef(null);
  const rtcPollRef = useRef(null);
  const videoRef = useRef(null);
  const scrollRef = useRef(null);
  const pcRef = useRef(null);
  const answeredRef = useRef(false);
  const addedCandsRef = useRef(new Set());

  const cleanupRtc = () => {
    if (pcRef.current) {
      try { pcRef.current.close(); }
      catch (err) { console.debug("[LiveViewer] cleanup pc.close", err?.message); }
      pcRef.current = null;
    }
    if (rtcPollRef.current) { clearInterval(rtcPollRef.current); rtcPollRef.current = null; }
    answeredRef.current = false;
    addedCandsRef.current = new Set();
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  const ensurePeer = () => {
    if (pcRef.current) return pcRef.current;
    const pc = new RTCPeerConnection(RTC_CONFIG);
    pc.ontrack = (ev) => {
      if (videoRef.current) {
        videoRef.current.srcObject = ev.streams[0];
        videoRef.current.play().catch(() => {});
      }
      setConnState("connected");
    };
    pc.onconnectionstatechange = () => {
      if (["failed", "closed", "disconnected"].includes(pc.connectionState)) {
        setConnState("connecting");
      }
    };
    pcRef.current = pc;
    return pc;
  };

  const rtcTick = async (id) => {
    if (!id) return;
    try {
      const r = await axios.get(`${API}/lives/${id}/offer`);
      const off = r.data.offer;
      if (off && !answeredRef.current) {
        const pc = ensurePeer();
        await pc.setRemoteDescription({ type: off.type, sdp: off.sdp });
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        await waitIceComplete(pc);
        await axios.post(`${API}/lives/${id}/answer`, {
          sdp: pc.localDescription.sdp,
          type: pc.localDescription.type,
        });
        answeredRef.current = true;
      }
      const cands = r.data.candidates || [];
      const pc = pcRef.current;
      if (pc) {
        for (const c of cands) {
          if (!c || !c.candidate) continue;
          const key = `${c.candidate}|${c.sdpMid}|${c.sdpMLineIndex}`;
          if (addedCandsRef.current.has(key)) continue;
          addedCandsRef.current.add(key);
          try { await pc.addIceCandidate(c); }
          catch (err) { console.debug("[LiveViewer] addIceCandidate skipped", err?.message); }
        }
      }
    } catch (err) {
      console.error("[LiveViewer] rtcTick failed", err);
    }
  };

  useEffect(() => {
    if (!liveId) { setLive(null); cleanupRtc(); setConnState("connecting"); return; }
    let cancelled = false;
    const join = async () => {
      try { await axios.post(`${API}/lives/${liveId}/join`); }
      catch (err) { console.error("[LiveViewer] join failed", err); }
      const load = async () => {
        try {
          const r = await axios.get(`${API}/lives/${liveId}`);
          if (cancelled) return;
          setLive(r.data);
          if (!r.data.is_active) {
            setConnState("ended");
            if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
            if (rtcPollRef.current) { clearInterval(rtcPollRef.current); rtcPollRef.current = null; }
          }
        } catch {
          if (cancelled) return;
          toast.error("Transmissão indisponível");
          onClose?.();
        }
      };
      await load();
      pollRef.current = setInterval(load, 3000);
      rtcPollRef.current = setInterval(() => { rtcTick(liveId); }, 2000);
    };
    join();
    return () => {
      cancelled = true;
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      if (rtcPollRef.current) { clearInterval(rtcPollRef.current); rtcPollRef.current = null; }
      cleanupRtc();
      axios.post(`${API}/lives/${liveId}/leave`).catch(() => {});
    };
  }, [liveId]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendMessage = async () => {
    if (!liveId || !chatInput.trim()) return;
    try {
      await axios.post(`${API}/lives/${liveId}/chat`, { message: chatInput.trim() });
      setChatInput("");
    } catch (e) {
      toast.error("Não foi possível enviar");
    }
  };

  const open = !!liveId;

  // Reactions overlay + push
  const reactionsCtl = LiveReactionsLayer({
    reactions: live?.reactions || [],
    totals: live?.reaction_totals || {},
    testIdPrefix: "live-viewer",
  });

  const sendReaction = async (type) => {
    if (!liveId) return;
    reactionsCtl.push(type); // optimistic
    try { await axios.post(`${API}/lives/${liveId}/reaction`, { type }); }
    catch (err) { console.error("[LiveViewer] reaction failed", err); }
  };

  // Big prominent "like" button — sends heart reaction with burst effect
  const sendLike = () => {
    setLikeBurst((n) => n + 1);
    // Spawn 3 hearts for extra visual impact
    for (let i = 0; i < 3; i++) {
      setTimeout(() => reactionsCtl.push("heart"), i * 120);
    }
    // Only 1 network call — server counts the "like" toward totals
    axios.post(`${API}/lives/${liveId}/reaction`, { type: "heart" })
      .catch((err) => console.debug("[LiveViewer] like batch failed", err?.message));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        className="max-w-6xl w-[95vw] p-0 overflow-hidden border-zinc-800 bg-black gap-0"
        data-testid="live-viewer-dialog"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 h-[90vh] md:h-[85vh]">
          {/* --- VIDEO STAGE --- */}
          <div className={`bg-black relative overflow-hidden ${chatCollapsed ? "md:col-span-3" : "md:col-span-2"}`}>
            {/* Real WebRTC stream */}
            <video
              ref={videoRef}
              data-testid="live-viewer-video"
              className={`absolute inset-0 w-full h-full object-contain bg-black ${connState === "connected" ? "opacity-100" : "opacity-0"} transition-opacity duration-500`}
              autoPlay
              playsInline
              muted={muted}
            />

            {/* Placeholder while connecting or ended */}
            {connState !== "connected" && (
              <>
                <div className="absolute inset-0 bg-gradient-to-br from-red-500/20 via-black to-primary/20" aria-hidden />
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-white p-6 text-center">
                  {live?.streamer_picture ? (
                    <Avatar className="w-24 h-24 border-4 border-red-500 shadow-2xl shadow-red-500/50 animate-pulse">
                      <AvatarImage src={live.streamer_picture} />
                      <AvatarFallback className="text-2xl">{live.streamer_name?.[0]}</AvatarFallback>
                    </Avatar>
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-red-500/20 border-2 border-red-500 flex items-center justify-center animate-pulse">
                      <Radio className="w-10 h-10 text-red-500" />
                    </div>
                  )}
                  <div>
                    <div className="text-3xl font-bold tracking-tight">{live?.streamer_name || "..."}</div>
                    <div className="text-sm text-white/70 mt-1">
                      {connState === "ended" || (live && !live.is_active) ? "encerrou a transmissão" : "conectando ao vídeo..."}
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Top gradient overlay for readability */}
            {live?.is_active && (
              <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-black/70 to-transparent pointer-events-none z-[1]" aria-hidden />
            )}
            {/* Bottom gradient overlay */}
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/80 to-transparent pointer-events-none z-[1]" aria-hidden />

            {/* --- TOP-LEFT: LIVE badge + viewer count --- */}
            {live?.is_active && (
              <div className="absolute top-3 left-3 flex items-center gap-2 z-10">
                <span
                  data-testid="live-viewer-count-badge"
                  className="bg-red-500 text-white text-[11px] font-bold uppercase tracking-widest px-2.5 py-1 rounded flex items-center gap-1.5 shadow-lg shadow-red-500/40"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" aria-hidden />
                  Ao Vivo
                </span>
                <span className="bg-black/60 backdrop-blur-md text-white text-xs px-2.5 py-1 rounded flex items-center gap-1.5 font-semibold">
                  <Users className="w-3.5 h-3.5" />
                  <span data-testid="live-viewer-count">{live?.viewer_count || 0}</span>
                </span>
                {live?.is_recording && (
                  <span
                    data-testid="live-viewer-recording-badge"
                    title="Esta transmissão está sendo gravada e será publicada no perfil do transmissor"
                    className="bg-black/60 backdrop-blur-md text-white text-[11px] px-2.5 py-1 rounded flex items-center gap-1.5 font-semibold"
                  >
                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" aria-hidden />
                    Gravando
                  </span>
                )}
              </div>
            )}
            {live?.is_active && live?.is_recording && connState !== "ended" && (
              <div
                data-testid="live-viewer-recording-notice"
                className="absolute bottom-24 left-3 right-3 md:right-auto md:max-w-sm z-10 bg-black/65 backdrop-blur-md text-white text-[11px] px-3 py-2 rounded-lg border border-red-500/40"
              >
                <span className="font-bold text-red-300">Aviso:</span> esta live está sendo gravada e ficará disponível no perfil de {live.streamer_name} após o término.
              </div>
            )}
            {live && !live.is_active && (
              <div className="absolute top-3 left-3 z-10">
                <span className="text-xs bg-zinc-900/80 backdrop-blur-md text-white px-2.5 py-1 rounded font-semibold uppercase tracking-widest">Encerrada</span>
              </div>
            )}

            {/* --- TOP-RIGHT: streamer identity + title + close --- */}
            <div className="absolute top-3 right-3 flex items-center gap-2 max-w-[70%] z-10">
              <button
                onClick={() => onClose?.()}
                data-testid="live-viewer-close"
                className="w-9 h-9 rounded-full bg-black/60 backdrop-blur-md text-white flex items-center justify-center hover:bg-black/90 transition-colors"
                aria-label="Fechar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {live && (
              <div className="absolute top-14 left-3 md:left-3 max-w-[70%] flex items-center gap-2 bg-black/50 backdrop-blur-md rounded-full pl-1 pr-3 py-1 z-10">
                <Avatar className="w-7 h-7">
                  <AvatarImage src={live.streamer_picture} />
                  <AvatarFallback>{live.streamer_name?.[0]}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <div className="text-white text-xs font-semibold leading-tight truncate" data-testid="live-viewer-title">{live.title}</div>
                  <div className="text-white/70 text-[10px] leading-tight truncate">{live.streamer_name}</div>
                </div>
              </div>
            )}

            {/* --- TOP-RIGHT (below close): mute --- */}
            {connState === "connected" && (
              <button
                onClick={() => setMuted((v) => !v)}
                data-testid="live-viewer-mute-toggle"
                className="absolute top-14 right-3 w-9 h-9 rounded-full bg-black/60 backdrop-blur-md text-white flex items-center justify-center hover:bg-black/90 transition-colors z-10"
                aria-label={muted ? "Ativar som" : "Silenciar"}
              >
                {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
            )}

            {/* --- REACTIONS: floating overlay + top-right totals --- */}
            {reactionsCtl.overlay}

            {/* --- CHAT COLLAPSE toggle (mobile hidden, desktop bottom-right) --- */}
            <button
              onClick={() => setChatCollapsed((v) => !v)}
              data-testid="live-viewer-chat-toggle"
              className="hidden md:flex absolute top-24 right-3 w-9 h-9 rounded-full bg-black/60 backdrop-blur-md text-white items-center justify-center hover:bg-black/90 transition-colors z-10"
              aria-label={chatCollapsed ? "Mostrar chat" : "Esconder chat"}
            >
              <MessageCircle className="w-4 h-4" />
            </button>

            {/* --- BOTTOM: reactions bar + like button --- */}
            {live?.is_active && (
              <div className="absolute bottom-0 left-0 right-0 z-10 flex items-end gap-2 p-3 pointer-events-none">
                <div className="flex-1 pointer-events-auto">
                  <LiveReactionsBar onReact={sendReaction} testIdPrefix="live-viewer" />
                </div>
                <button
                  onClick={sendLike}
                  data-testid="live-viewer-like-btn"
                  className="pointer-events-auto flex-shrink-0 w-14 h-14 rounded-full bg-gradient-to-br from-red-500 to-pink-500 shadow-lg shadow-red-500/40 text-white flex items-center justify-center hover:scale-110 active:scale-95 transition-transform"
                  aria-label="Curtir"
                >
                  <Heart className={`w-6 h-6 ${likeBurst > 0 ? "fill-current animate-ping-once" : "fill-current"}`} />
                </button>
              </div>
            )}
          </div>

          {/* --- CHAT SIDEBAR --- */}
          {!chatCollapsed && (
            <div className="border-l border-zinc-800 flex flex-col bg-zinc-950 md:col-span-1 min-h-0">
              <div className="p-3 border-b border-zinc-800 flex items-center justify-between">
                <div className="text-xs uppercase tracking-wider font-bold text-zinc-400 flex items-center gap-1.5">
                  <MessageCircle className="w-3.5 h-3.5" /> Chat ao vivo
                </div>
                <button
                  onClick={() => setChatCollapsed(true)}
                  data-testid="live-viewer-chat-close"
                  className="md:block hidden text-zinc-500 hover:text-white transition-colors"
                  aria-label="Esconder chat"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <ScrollArea className="flex-1 p-3" ref={scrollRef}>
                <div className="space-y-2" data-testid="live-viewer-chat">
                  {(!live?.chat || live.chat.length === 0) && (
                    <div className="text-xs text-zinc-500 text-center py-8">Seja o primeiro a falar!</div>
                  )}
                  {(live?.chat || []).map((m) => (
                    <div key={m.msg_id} className="flex items-start gap-2 text-sm">
                      <Avatar className="w-6 h-6 flex-shrink-0">
                        <AvatarImage src={m.author_picture} />
                        <AvatarFallback>{m.author_name?.[0]}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="text-[11px] font-semibold text-zinc-400">{m.author_name}</div>
                        <div className="text-sm text-zinc-100 break-words">{m.message}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              {live?.is_active && (
                <div className="p-3 border-t border-zinc-800 flex gap-2">
                  <Input
                    data-testid="live-viewer-chat-input"
                    placeholder="Diga algo..."
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                    className="bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 focus-visible:ring-red-500"
                  />
                  <Button data-testid="live-viewer-chat-send" size="icon" onClick={sendMessage} className="rounded-full flex-shrink-0 bg-red-500 hover:bg-red-600 text-white">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
